import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { CheckIcon, Loader2Icon, RefreshCcwIcon } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { updateProfileSchema } from '@shared/schemas/auth';
import {
  getBrowserUrl,
  sendVerificationEmail,
  updateCurrentUser,
} from '@/lib/auth-client';
import { getAuthErrorMessage } from '@/lib/auth-errors';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

type AccountProfileFormProps = React.ComponentProps<'form'> & {
  onAccountUpdated: () => Promise<void>;
  session: NonNullable<ReturnType<typeof import('@/lib/auth-client').authClient.useSession>['data']>;
};

type UpdateProfileFormInput = z.input<typeof updateProfileSchema>;
type UpdateProfileFormOutput = z.output<typeof updateProfileSchema>;

export function AccountProfileForm({
  className,
  onAccountUpdated,
  session,
  ...props
}: AccountProfileFormProps) {
  const form = useForm<UpdateProfileFormInput, undefined, UpdateProfileFormOutput>({
    defaultValues: {
      image: session.user.image ?? '',
      name: session.user.name,
    },
    resolver: zodResolver(updateProfileSchema),
  });

  useEffect(() => {
    form.reset({
      image: session.user.image ?? '',
      name: session.user.name,
    });
  }, [form, session.user.image, session.user.name]);

  const isBusy = form.formState.isSubmitting;

  async function handleSubmit(values: UpdateProfileFormOutput) {
    form.clearErrors('root');

    const nextImage = values.image ?? null;
    const currentImage = session.user.image ?? null;

    if (values.name === session.user.name && nextImage === currentImage) {
      toast.info('Aucune modification à enregistrer.');
      return;
    }

    const result = await updateCurrentUser({
      ...values,
      image: nextImage,
    });

    if (result.error) {
      const message = getAuthErrorMessage(result.error);
      form.setError('root', { message });
      toast.error(message);
      return;
    }

    form.setValue('image', nextImage ?? '', {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: false,
    });
    await onAccountUpdated();
    toast.success('Profil mis à jour.');
  }

  async function handleResendVerificationEmail() {
    const result = await sendVerificationEmail(
      session.user.email,
      getBrowserUrl(`/auth/verify-email?email=${encodeURIComponent(session.user.email)}`),
    );

    if (result.error) {
      toast.error(getAuthErrorMessage(result.error));
      return;
    }

    toast.success('Un nouveau lien de confirmation a été envoyé.');
  }

  return (
    <form className={cn('rounded-[var(--radius-surface-4xl)] border border-border bg-card p-6 shadow-[0_8px_24px_rgba(15,23,42,0.03)]', className)} onSubmit={form.handleSubmit(handleSubmit)} {...props}>
      <FieldGroup className="gap-6">
        <div className="space-y-2">
          <h2 className="text-2xl font-semibold text-foreground">Informations du compte</h2>
          <p className="text-sm leading-6 text-muted-foreground">
            Le nom reste modifiable ici. L'email et le rôle sont pilotés par le système d'administration.
          </p>
        </div>
        <input type="hidden" {...form.register('image')} />

        <div className="grid gap-6 lg:grid-cols-2">
          <Field>
            <FieldLabel htmlFor="account-name">Nom complet <span className="text-destructive">*</span></FieldLabel>
            <Input
              id="account-name"
              data-testid="account-name-input"
              type="text"
              autoComplete="name"
              aria-invalid={form.formState.errors.name ? 'true' : 'false'}
              {...form.register('name')}
            />
            <FieldError errors={[form.formState.errors.name]} />
          </Field>

          <Field>
            <FieldLabel htmlFor="account-email">Email <span className="text-destructive">*</span></FieldLabel>
            <Input id="account-email" value={session.user.email} type="email" disabled readOnly />
            <FieldDescription>
              {session.user.emailVerified ? 'Email déjà vérifié.' : 'Email non vérifié.'}
            </FieldDescription>
          </Field>
        </div>

        <div className="-mt-2 grid gap-6 lg:grid-cols-2">
          <Field>
            <FieldLabel htmlFor="account-role">Rôle</FieldLabel>
            <Input
              id="account-role"
              value={((session.user as { role?: string | null }).role ?? 'user') === 'admin' ? 'Administrateur' : 'Utilisateur'}
              disabled
              readOnly
            />
            <FieldDescription>Le rôle est défini par les règles d'administration.</FieldDescription>
          </Field>
          <div className="hidden lg:block" />
        </div>

        {!session.user.emailVerified ? (
          <div className="rounded-[var(--radius-surface-3xl)] border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            <p className="leading-6">
              Ton email n'est pas encore vérifié. Certaines opérations de sécurité peuvent rester limitées tant que ce
              point n'est pas résolu.
            </p>
            <Button
              type="button"
              variant="outline"
              className="mt-4 border-amber-300 bg-background text-amber-900 hover:bg-amber-100 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-200 dark:hover:bg-amber-950/50"
              onClick={() => void handleResendVerificationEmail()}
            >
              <RefreshCcwIcon />
              Renvoyer le lien de confirmation
            </Button>
          </div>
        ) : null}

        <div className="flex justify-end">
          <Button
            data-testid="account-profile-submit"
            type="submit"
            disabled={isBusy}
            className="h-[49px] gap-2 border-black bg-black px-6 text-[15px] font-medium text-white shadow-none transition-colors hover:border-black/85 hover:bg-black/85 hover:text-white dark:border-border dark:bg-muted dark:text-foreground dark:hover:border-foreground/20 dark:hover:bg-muted/80 dark:hover:text-foreground"
          >
            {isBusy ? <Loader2Icon className="animate-spin" /> : <CheckIcon />}
            Enregistrer
          </Button>
        </div>
      </FieldGroup>
    </form>
  );
}
